/**
 * Formats a phone number by adding parentheses and dash like (123) 456-7890.
 * If displayOnly is true and the number has any non-digit characters, it is returned as is.
 *
 * @param {string} phoneNumber - The phone number to format.
 * @param {boolean} [displayOnly] - Indicates if the phone number is only being formatted for display. This will prevent formatting if the number has any non-digits in it.
 * @returns {string} The formatted phone number.
 */
const formattedPhoneNumber = (phoneNumber, displayOnly) => {
  if (!phoneNumber) {
    // eslint-disable-next-line no-param-reassign
    phoneNumber = '';
  }

  // If the number has any non-digit characters, return it as is
  if (displayOnly && /\D/g.test(phoneNumber)) {
    return phoneNumber;
  }

  // eslint-disable-next-line no-param-reassign
  phoneNumber = phoneNumber.replace(/\D/g, '');
  const phoneNumberLength = phoneNumber.length;

  if (phoneNumberLength < 4 || phoneNumberLength > 10) return phoneNumber;

  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }

  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
    3,
    6,
  )}-${phoneNumber.slice(6, 10)}`;
};

/**
 * Parses a phone number as digits (removing any non-digit characters) and gets the cursor position in that digit only value.
 *
 * @param {string} value - The phone number value.
 * @param {number} displayedCursorPosition - The cursor position in the displayed value.
 * @returns {object} - An object containing the parsed value and the cursor position.
 */
const parsePhoneNumberAsDigitsAndGetCursorPosition = (
  value,
  displayedCursorPosition,
) => {
  let parsedValue = '';
  let selectionIndex = 0;
  let index = 0;

  while (index < value.length) {
    const character = /\d/.test(value[index]) ? value[index] : undefined;

    if (character !== undefined) {
      parsedValue += character;

      if (displayedCursorPosition !== undefined) {
        if (displayedCursorPosition === index) {
          selectionIndex = parsedValue.length - 1;
        } else if (displayedCursorPosition > index) {
          selectionIndex = parsedValue.length;
        }
      }
    }

    index += 1;
  }

  // If displayedCursorPosition position wasn't specified
  if (displayedCursorPosition === undefined) {
    // Then set displayedCursorPosition position to "after the last input character"
    selectionIndex = parsedValue.length;
  }

  return {
    parsedValue,
    cursorPosition: selectionIndex,
  };
};

/**
 * Calculates the new cursor position after formatting a phone number.
 *
 * @param {string} value - The phone number value.
 * @param {number} [cursorPosition] - The current cursor position.
 * @returns {number} - The new cursor position after formatting.
 */
const getPhoneNumberCursorPositionAfterFormatting = (value, cursorPosition) => {
  const template = '(xxx) xxx-xxxx';
  let newCursorPosition = cursorPosition;
  if (newCursorPosition === undefined) {
    newCursorPosition = value.length;
  } else {
    let index = 0;
    let found = false;

    let possiblyLastInputCharacterIndex = -1;

    while (index < value.length) {
      // Character placeholder found
      if (value[index] !== template[index]) {
        if (newCursorPosition === 0) {
          found = true;
          newCursorPosition = index;
          break;
        }

        possiblyLastInputCharacterIndex = index;

        newCursorPosition -= 1;
      }

      index += 1;
    }

    // If the newCursorPosition was positioned after last input character,
    // then the value newCursorPosition index is just after the last input character.
    if (!found) {
      newCursorPosition = possiblyLastInputCharacterIndex + 1;
    }
  }

  return newCursorPosition;
};

/**
 * Sets the new value and cursor position of the provided input - the input value needs to be changed directly for some weird react reason. If the input value is different than the value that we, later, provide to setState() then the cursor will be put at the end of the input value (even though we set it here).
 *
 * @param {HTMLInputElement} inputElement - The input element.
 * @param {string} value - The new value for the input element.
 * @param {number} cursorPosition - The new cursor position for the input element.
 */
const setValueAndCursorPositionOfInput = (
  inputElement,
  value,
  cursorPosition,
) => {
  // eslint-disable-next-line no-param-reassign
  inputElement.value = value;
  inputElement.setSelectionRange?.(cursorPosition, cursorPosition);
};

/**
 * Formats the phone input value and ensures that the cursor in the input is set to the correct location after the edit.
 *
 * @param {HTMLInputElement} inputElement - The input element to format.
 * @returns {string} The formatted phone number value.
 */
const formatPhoneInputAndSetCursorPosition = (inputElement) => {
  const {
    parsedValue,
    cursorPosition,
  } = parsePhoneNumberAsDigitsAndGetCursorPosition(
    inputElement.value,
    inputElement.selectionStart,
  );
  const formattedValue = formattedPhoneNumber(parsedValue);
  const newCursorPosition = getPhoneNumberCursorPositionAfterFormatting(
    formattedValue,
    cursorPosition,
  );

  setValueAndCursorPositionOfInput(
    inputElement,
    formattedValue,
    newCursorPosition,
  );

  return formattedValue;
};

export default {
  formattedPhoneNumber,
  parsePhoneNumberAsDigitsAndGetCursorPosition,
  getPhoneNumberCursorPositionAfterFormatting,
  setValueAndCursorPositionOfInput,
  formatPhoneInputAndSetCursorPosition,
};
